import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { getAppAuthBoundary, signOut } from '../modules/auth';

/**
 * The one async Bearer-attaching `baseQuery` every RTK Query client in Dashboard is meant to
 * build from (T030; FR-008, US3 "use one access-token contract across every request client").
 *
 * Before this task each of the five RTK Query clients (`my-stats-api.jsx`, `onboarding-api.jsx`,
 * `companies-api.js`, `subscription-api.jsx`, `users-api.jsx`) built its own `fetchBaseQuery` with
 * an identical, independently-copied, SYNCHRONOUS `prepareHeaders` that read
 * `helpers/auth-header.jsx`'s `getAuthToken()` — a legacy-only cookie read with no SSO awareness
 * and no path through `getAppAuthBoundary()` (FR-008 requires exactly one boundary for every read,
 * mode-neutral). Rather than replace that one copied block with five new, still-independent
 * copies (this repo's own "reuse before duplicating" convention — see this task's own wording,
 * "the SAME async Bearer boundary ... with PARITY coverage"), this single helper is the one
 * implementation all five now import and call identically, so parity is structural (one function)
 * rather than merely observed (five call sites that happen to agree today but could drift later).
 *
 * **Why `prepareHeaders` is `async` here** (not merely reading a cached token): a request client
 * must be able to attempt the boundary's one-shot silent renewal before attaching a token that may
 * already be expired (`auth-boundary.ts`'s `ensureFreshSession()`, T027) — `fetchBaseQuery`'s
 * `prepareHeaders` type is `(headers, api) => MaybePromise<Headers | void>` (confirmed against the
 * installed `@reduxjs/toolkit@1.9.7` — `node_modules/@reduxjs/toolkit/dist/query/fetchBaseQuery.d.ts`),
 * so an `async` function is natively supported without any custom `baseQuery` wrapper. `getAccessToken()`
 * is read AFTER `ensureFreshSession()` settles (either outcome) so a request always carries the
 * freshest token the boundary can produce without a second boundary round trip; when neither an SSO
 * nor a legacy session exists (or renewal settles `interaction_required` with no token at all), no
 * `Authorization` header is attached at all, matching every one of the five clients' prior
 * behavior for an absent token.
 *
 * This is this task's one declared PR-scope amendment (6 files -> 7): a genuinely new shared file
 * was necessary to avoid duplicating boundary-reading logic five times, mirroring how T020/T024's
 * amendments in this same feature were documented rather than made silently (see orchestration.md).
 */
export function createBearerBaseQuery(
  baseUrl: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      const authBoundary = getAppAuthBoundary();
      await authBoundary.ensureFreshSession();
      const token = authBoundary.getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

  // A 401 means the session is gone: hand it to the one sign-out owner, as the Apollo and apisauce
  // clients do (FR-021, FR-048; T108). `signOut` runs once per page, so concurrent 401s collapse.
  // A 403 only means this endpoint refused this user, so it stays with the calling API.
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    if (result.error?.status === 401 && !window.location.pathname.startsWith('/user/logout')) {
      void signOut();
    }
    return result;
  };
}
